import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import Debounce from '../Utils/Debounce';

if (!Element.prototype.matches)
	Element.prototype.matches =
		Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;

if (!Element.prototype.closest) {
	Element.prototype.closest = function (s) {
		var el = this;

		do {
			if (Element.prototype.matches.call(el, s)) return el;
			el = el.parentElement || el.parentNode;
		} while (el !== null && el.nodeType === 1);
		return null;
	};
}
window.requestAnimationFrame = window.requestAnimationFrame.bind(window);

gsap.registerPlugin(ScrollTrigger);
gsap.core.globals('ScrollTrigger', ScrollTrigger);
ScrollTrigger.config({ limitCallbacks: true });

const allAnims = [];
const mobileSize = 768;
const scrollerElems = [].slice.call(document.querySelectorAll('.js-scroller'));

/* 
	Debug Options
*/
const debug = {
	showMarkers: { mobile: false, largeScreens: false },
};

class FancyAnimation {
	tl;
	pauseTl;
	revealedElem;
	videoPoster;

	constructor(elem) {
		this.maskElem = elem.querySelector('.js-animation-svg');
		this.maskHeight = elem.querySelector('.js-animation-svg');
		this.maskWidth = elem.querySelector('.js-animation-svg');
		this.containerElem = elem.querySelector('.js-revealed-container');
		this.revealedElem = elem.querySelector('.js-revealed');
		this.destruct = this.destruct.bind(this);
		this.toggleTransform = this.toggleTransform.bind(this);
		this.adjustSize = this.adjustSize.bind(this);
	}

	// Clears props of dom elements that are used as scroll triggers
	destruct() {
		gsap.set(this.maskElem, { clearProps: 'top,width,height' });
		gsap.set(this.containerElem, { clearProps: true });
	}

	// Resets transform property of parent u-break-grid element, so that position:fixed applies
	toggleTransform(reset = true) {
		reset
			? this.containerElem.closest('.u-break-grid').classList.add('reset-transform')
			: this.containerElem.closest('.u-break-grid').classList.remove('reset-transform');
	}

	// Creates props for scaling animation
	createScalingProps(multiplier) {
		let propObj = {
			height: this.maskElem.height * multiplier,
			width: this.maskElem.width * multiplier,
			transform: `translate(-50%, ${-(this.maskElem.height * multiplier) / 2}px)`,
		};

		return propObj;
	}

	adjustSize() {
		this.revealedElem.style.height = this.maskElem.height - 2 + 'px';

		this.maskElem.classList.remove('wide', 'tall');
		if (window.innerWidth > window.innerHeight) {
			this.maskElem.classList.add('wide');
		} else this.maskElem.classList.add('tall');
	}
}

class FancyAnimationVideo extends FancyAnimation {
	constructor(el) {
		super(el);
		this.vid = el.querySelector('.js-vid');
		this.videoPoster = this.vid.poster;
		this.playVid = this.playVid.bind(this);
		this.pauseVid = this.pauseVid.bind(this);
		this.pauseVid();
	}

	// Constructs scroll triggers and timeline for small screens
	constructMobile() {
		this.adjustSize();
		this.tl = gsap.timeline({
			scrollTrigger: {
				trigger: this.maskElem,
				start: '100% 50%',
				markers: debug.showMarkers.mobile,
				onEnter: this.playVid,
				onEnterBack: this.playVid,
				onLeave: this.pauseVid,
				onLeaveBack: this.pauseVid,
			},
		});
	}

	// Constructs scroll triggers and timeline for large screens
	construct() {
		if (window.innerWidth <= mobileSize) {
			this.constructMobile();
			return;
		}

		this.adjustSize();
		this.tl = gsap.timeline({
			scrollTrigger: {
				trigger: this.maskElem,
				start: '0% 0%',
				end: '80% -140%',
				markers: debug.showMarkers.largeScreens,
				scrub: true,
				pin: this.containerElem,
				pinSpacing: true,
				toggleActions: 'restart none none reverse',
				onEnter: this.toggleTransform,
				onEnterBack: () => {
					this.toggleTransform();
					this.playVid();
				},
			},
			onComplete: () => this.toggleTransform(false),
			onReverseComplete: () => this.toggleTransform(false),
		});

		this.pauseTl = gsap.timeline({
			scrollTrigger: {
				trigger: this.containerElem,
				start: '100% 60%',
				markers: debug.showMarkers.largeScreens,
				onEnter: this.pauseVid,
			},
		});

		this.tl
			.to(this.maskElem, {
				...this.createScalingProps(50),
				duration: 5,
				top: '50%',
				ease: 'Power3.easeIn',
			})
			.call(() => {
				this.toggleContent(false);
				this.maskElem.classList.remove('transparent');
			})
			.call(() => {
				this.toggleContent();
				this.maskElem.classList.add('transparent');
			})
			.to(this.maskElem, {
				...this.createScalingProps(200),
				duration: 2,
			})
			.to(this.maskElem, {
				display: 'none',
			});
	}

	toggleContent(show = true) {
		this.revealedElem.classList[show ? 'add' : 'remove']('show');
		show ? this.playVid() : this.pauseVid();
	}

	// Plays the video element contained within the .js-scroller element
	playVid() {
		this.vid.poster = '';
		this.vid.play();
	}

	// Pauses the video element contained within the .js-scroller element
	pauseVid() {
		this.vid.poster = this.videoPoster;
		this.vid.pause();
	}
}

class FancyAnimationImage extends FancyAnimation {
	revealedImg;

	constructor(el) {
		super(el);
	}

	constructMobile() {}

	// Constructs scroll triggers and timeline for large screens
	construct() {
		if (window.innerWidth <= mobileSize) {
			this.constructMobile();
			return;
		}

		this.adjustSize();
		this.tl = gsap.timeline({
			scrollTrigger: {
				trigger: this.maskElem,
				start: '0% 0%',
				end: '80% -140%',
				markers: debug.showMarkers.largeScreens,
				scrub: true,
				pin: this.containerElem,
				pinSpacing: true,
				toggleActions: 'restart none none reverse',
				onEnter: this.toggleTransform,
				onEnterBack: this.toggleTransform,
				immediateRender: false,
			},
			immediateRender: false,
			onComplete: () => this.toggleTransform(false),
			onReverseComplete: () => this.toggleTransform(false),
		});

		this.pauseTl = gsap.timeline({
			scrollTrigger: {
				trigger: this.containerElem,
				start: '100% 60%',
				markers: debug.showMarkers.largeScreens,
			},
		});

		this.tl
			.to(this.maskElem, {
				...this.createScalingProps(40),
				duration: 4,
				top: '50%',
				ease: 'Power3.easeIn',
			})
			.call(() => {
				this.toggleContent(false);
				this.maskElem.classList.remove('transparent');
			})
			.call(() => {
				this.toggleContent();
				this.maskElem.classList.add('transparent');
			})
			.to(this.maskElem, {
				...this.createScalingProps(200),
				duration: 2,
			})
			.to(this.maskElem, {
				display: 'none',
			});
	}

	toggleContent(show = true) {
		this.revealedElem.classList[show ? 'add' : 'remove']('show');
	}
}

const initializeFancyAnimations = () => {
	// create new FancyAnimations out of all the .js-scroller dom elements
	scrollerElems.forEach(el => {
		let anim;
		if (el.querySelector('.js-vid')) anim = new FancyAnimationVideo(el);
		else anim = new FancyAnimationImage(el);
		allAnims.push(anim);
	});

	// Construct all the scroll triggers & timelines depending on screensize
	allAnims.forEach(anim => {
		window.innerWidth <= mobileSize ? anim.constructMobile() : anim.construct();
	});

	scrollerElems.forEach(el => {
		el.closest('.u-break-grid').classList.remove('reset-transform');
	});
};

initializeFancyAnimations();

// Kill & resconstruct scrolltriggers & clear props on resize
const resizeScrollAnimations = () => {
	scrollerElems.forEach(el => {
		el.closest('.u-break-grid').classList.remove('reset-transform');
	});

	const Alltriggers = ScrollTrigger.getAll();
	for (let i = 0; i < Alltriggers.length; i++) Alltriggers[i].kill(true);

	allAnims.forEach(anim => {
		anim.destruct();
		anim.construct();
	});
};

window.addEventListener('resize', Debounce(resizeScrollAnimations, 250));
3;
